import React from 'react'
import {
	Card,
	Col,
	Row,
	Input,
	Select,
	Button,
	InputNumber,
	Typography,
	Form as AntForm,
	message,
} from 'antd'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSendPushNotificationMutation } from 'modules/push-notifications/api'
import { useTranslation } from 'react-i18next'
import { schema, ValidationSchemaType } from './schema'
import { useGetFrontendVersionQuery } from 'modules/appConfig/requests/queries'

const { Option } = Select
const { Title } = Typography

export const SendPush: React.FC = () => {
	const { t } = useTranslation('pushNotifications', { keyPrefix: 'sendPush' })
	const [sendPush] = useSendPushNotificationMutation()
	const { data: versionData } = useGetFrontendVersionQuery()

	const {
		control,
		handleSubmit,
		watch,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<ValidationSchemaType>({
		resolver: yupResolver(schema),
		defaultValues: {
			contentType: 'empty',
			languageSelect: 'yi',
		},
	})

	const contentType = watch('contentType')

	const onSubmit = async (values: ValidationSchemaType) => {
		const isVersionUpdate = values.contentType === 'versionUpdate'

		const commonData = {
			id: values?.contentId,
			contentType: values.contentType,
			latestVersion: isVersionUpdate ? versionData?.frontendVersion : undefined,
			language:
				values.contentType === 'home' ? values.languageSelect : undefined,
		}

		const data = isVersionUpdate
			? { ...commonData, latestVersion: versionData?.frontendVersion }
			: commonData

		try {
			await sendPush({
				title: values.title,
				body: values.body,
				contentType: values.contentType,
				data,
			}).unwrap()

			message.success(t('success', 'Push notification sent successfully'))
			reset({ contentType: 'empty' })
		} catch (e) {
			message.error(t('sendError', 'Failed to send push notification'))
			console.error('Error sending push notification:', e)
		}
	}

	return (
		<Card>
			<Row>
				<Col span={24}>
					<Title level={2}>{t('titlePage', 'Send push notification')}</Title>
				</Col>
				<Col span={24}>
					<form>
						<AntForm layout='vertical'>
							<AntForm.Item
								label={t('contentType')}
								validateStatus={errors.contentType ? 'error' : ''}
								help={errors.contentType?.message}
							>
								<Controller
									name='contentType'
									control={control}
									render={({ field }) => (
										<Select {...field}>
											<Option value='home'>{t('home')}</Option>
											<Option value='category'>{t('category')}</Option>
											<Option value='soundtrack'>{t('soundtrack')}</Option>
											<Option value='author'>{t('author')}</Option>
											<Option value='versionUpdate'>
												{t('versionUpdate')}
											</Option>
											<Option value='empty'>{t('empty')}</Option>
										</Select>
									)}
								/>
							</AntForm.Item>

							{contentType !== 'home' &&
								contentType !== 'versionUpdate' &&
								contentType !== 'empty' && (
									<AntForm.Item
										label={t('contentIdLabel', {
											type: `${contentType
												.charAt(0)
												.toUpperCase()}${contentType.slice(1)}`,
										})}
										validateStatus={errors.contentId ? 'error' : ''}
										help={errors.contentId?.message}
									>
										<Controller
											name='contentId'
											control={control}
											render={({ field }) => (
												<InputNumber
													{...field}
													style={{ width: '100%' }}
													placeholder={t(
														'contentIdPlaceholder',
														'Numerical ID'
													)}
												/>
											)}
										/>
									</AntForm.Item>
								)}

							{contentType === 'home' && (
								<AntForm.Item
									label={t('languageLabel')}
									validateStatus={errors.languageSelect ? 'error' : ''}
									help={errors.languageSelect?.message}
								>
									<Controller
										name='languageSelect'
										control={control}
										render={({ field }) => (
											<Select {...field}>
												<Option value='en'>{t('en')}</Option>
												<Option value='yi'>{t('yi')}</Option>
											</Select>
										)}
									/>
								</AntForm.Item>
							)}

							<AntForm.Item
								label={t('title')}
								validateStatus={errors.title ? 'error' : ''}
								help={errors.title?.message}
							>
								<Controller
									name='title'
									control={control}
									render={({ field }) => (
										<Input {...field} maxLength={50} placeholder={t('title')} />
									)}
								/>
							</AntForm.Item>

							<AntForm.Item
								label={t('body')}
								validateStatus={errors.body ? 'error' : ''}
								help={errors.body?.message}
							>
								<Controller
									name='body'
									control={control}
									render={({ field }) => (
										<Input.TextArea
											{...field}
											rows={4}
											maxLength={150}
											placeholder={t('body')}
										/>
									)}
								/>
							</AntForm.Item>

							<AntForm.Item>
								<Button
									type='primary'
									loading={isSubmitting}
									onClick={handleSubmit(onSubmit)}
								>
									{t('send')}
								</Button>
							</AntForm.Item>
						</AntForm>
					</form>
				</Col>
			</Row>
		</Card>
	)
}
